/* Feature gate definitions — single source of truth for tier-gated features.
   Used by useFeatureGate hook to determine access and show upgrade prompts. */

export type SubscriptionTier = "free" | "pro" | "pro_ai";

export type FeatureId =
  | "ai_chat"
  | "ai_digest"
  | "content_analysis"
  | "sources"
  | "email_sources"
  | "topic_sections"
  | "for_you_tab"
  | "feed_cleaner"
  | "provocative_filter"
  | "full_analytics"
  | "media_tracking"
  | "notes"
  | "unlimited_refreshes"
  | "digest_styles"
  | "ai_highlights"
  | "podcast_transcription"
  | "auto_skip"
  | "auto_analyze"
  | "ai_topics"
  | "topics_graph"
  | "insights_tab"
  | "media_notes"
  | "article_highlights"
  | "ai_autoplan"
  | "ai_goals"
  | "daily_overview_premium";

export interface FeatureGate {
  requiredTier: SubscriptionTier;
  freeLimit?: number;
  proLimit?: number;
  displayName: string;
  upgradeMessage: string;
}

export const FEATURE_GATES: Record<FeatureId, FeatureGate> = {
  ai_chat: {
    requiredTier: "pro_ai",
    freeLimit: 10,
    displayName: "AI Chat",
    upgradeMessage: "Upgrade to Pro AI for unlimited AI chat messages",
  },
  ai_digest: {
    requiredTier: "pro_ai",
    freeLimit: 4,
    displayName: "AI Daily Digest",
    upgradeMessage: "Upgrade to Pro AI for unlimited daily digests",
  },
  content_analysis: {
    requiredTier: "pro_ai",
    freeLimit: 5,
    displayName: "Content Analysis",
    upgradeMessage: "Upgrade to Pro AI for unlimited content analysis",
  },
  sources: {
    requiredTier: "pro",
    freeLimit: 50,
    displayName: "Sources",
    upgradeMessage: "Upgrade to Pro for unlimited sources",
  },
  email_sources: {
    requiredTier: "pro",
    displayName: "Email Sources",
    upgradeMessage: "Upgrade to Pro to add email newsletter sources",
  },
  topic_sections: {
    requiredTier: "pro",
    displayName: "Topic Sections",
    upgradeMessage: "Upgrade to Pro to organize your feed by topic",
  },
  for_you_tab: {
    requiredTier: "pro",
    displayName: "For You Tab",
    upgradeMessage: "Upgrade to Pro for personalized content recommendations",
  },
  feed_cleaner: {
    requiredTier: "pro",
    displayName: "Feed Cleaner",
    upgradeMessage: "Upgrade to Pro to automatically clean your feed",
  },
  provocative_filter: {
    requiredTier: "pro",
    displayName: "Provocative Content Filter",
    upgradeMessage: "Upgrade to Pro to filter provocative content",
  },
  full_analytics: {
    requiredTier: "pro",
    displayName: "Full Analytics",
    upgradeMessage: "Upgrade to Pro for detailed usage analytics",
  },
  media_tracking: {
    requiredTier: "pro",
    freeLimit: 3,
    displayName: "Media Tracking",
    upgradeMessage: "Upgrade to Pro for unlimited movies, books, TV shows, and albums",
  },
  notes: {
    requiredTier: "pro",
    freeLimit: 50,
    displayName: "Notes",
    upgradeMessage: "Upgrade to Pro for unlimited notes",
  },
  unlimited_refreshes: {
    requiredTier: "pro",
    displayName: "Unlimited Refreshes",
    upgradeMessage: "Upgrade to Pro for unlimited feed refreshes",
  },
  digest_styles: {
    requiredTier: "pro_ai",
    displayName: "Digest Styles",
    upgradeMessage: "Upgrade to Pro AI to customize your digest style",
  },
  ai_highlights: {
    requiredTier: "pro_ai",
    displayName: "AI Highlights",
    upgradeMessage: "Upgrade to Pro AI for AI-powered highlights",
  },
  podcast_transcription: {
    requiredTier: "pro_ai",
    displayName: "Podcast Transcription",
    upgradeMessage: "Upgrade to Pro AI for AI podcast transcription",
  },
  auto_skip: {
    requiredTier: "pro_ai",
    displayName: "Auto-Skip Segments",
    upgradeMessage: "Upgrade to Pro AI to auto-skip intros, outros & sponsors",
  },
  auto_analyze: {
    requiredTier: "pro_ai",
    displayName: "Auto-Analyze",
    upgradeMessage: "Upgrade to Pro AI for automatic content analysis",
  },
  ai_topics: {
    requiredTier: "pro_ai",
    displayName: "AI Topics",
    upgradeMessage: "Upgrade to Pro AI for AI-extracted topics",
  },
  topics_graph: {
    requiredTier: "pro_ai",
    displayName: "Topics Graph",
    upgradeMessage: "Upgrade to Pro AI for the topics visualization graph",
  },
  insights_tab: {
    requiredTier: "pro_ai",
    displayName: "Insights",
    upgradeMessage: "Upgrade to Pro AI for AI-powered content insights",
  },
  media_notes: {
    requiredTier: "pro_ai",
    displayName: "Media Notes",
    upgradeMessage: "Upgrade to Pro AI for timestamped media notes",
  },
  article_highlights: {
    requiredTier: "pro_ai",
    displayName: "Article Highlights",
    upgradeMessage: "Upgrade to Pro AI for article highlighting",
  },
  ai_autoplan: {
    requiredTier: "pro_ai",
    displayName: "AI Autoplan",
    upgradeMessage: "Upgrade to Pro AI for AI-powered daily planning",
  },
  ai_goals: {
    requiredTier: "pro_ai",
    displayName: "AI Goal Suggestions",
    upgradeMessage: "Upgrade to Pro AI for AI goal suggestions",
  },
  daily_overview_premium: {
    requiredTier: "pro",
    displayName: "Premium Overview",
    upgradeMessage: "Upgrade to Pro for queue health and advanced overview insights",
  },
};

/** Count-based limits for the free tier (non-feature-specific) */
export const FREE_TIER_LIMITS = {
  maxSources: 50,
  maxFeedItems: 1000,
  maxMoviesPerMonth: 3,
  maxBooksPerMonth: 1,
  maxTvShowsPerMonth: 1,
  maxAlbumsPerMonth: 3,
  maxNotesPerMonth: 50,
} as const;

export const TIER_DISPLAY_NAMES: Record<SubscriptionTier, string> = {
  free: "Free",
  pro: "Pro",
  pro_ai: "Pro AI",
};

export function tierLevel(tier: SubscriptionTier): number {
  const levels: Record<SubscriptionTier, number> = { free: 0, pro: 1, pro_ai: 2 };
  return levels[tier];
}

export function hasAccess(userTier: SubscriptionTier, requiredTier: SubscriptionTier): boolean {
  return tierLevel(userTier) >= tierLevel(requiredTier);
}
